import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { X, Download, Upload, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useBulkCreateEmployees } from '@/api/queries/employee.queries';
import { EMPLOYEE_FIELD_REGISTRY, getFieldsByCategory, FIELD_CATEGORIES } from '@/lib/employeeFields';

// Object/repeatable (nominee, family members, witnesses) and photo don't
// translate cleanly to a flat spreadsheet row — bulk upload deliberately
// only covers scalar fields. Anyone needing those can add them via Edit
// after the bulk import.
const BULK_EXCLUDED_TYPES = ['object', 'repeatable', 'photo'];

function getBulkFields(enabledList) {
  const fields = [];
  for (const cat of FIELD_CATEGORIES) {
    for (const f of getFieldsByCategory(cat.key, enabledList)) {
      if (f.key === 'employee_code' || BULK_EXCLUDED_TYPES.includes(f.type)) continue;
      fields.push(f);
    }
  }
  return fields;
}

function excelDateToISO(value) {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return null;
}

function validateRow(row, fields, departments) {
  const errors = {};
  const payload = { extended_details: {} };

  for (const f of fields) {
    const raw = row[f.label];
    const hasValue = raw !== undefined && raw !== null && String(raw).trim() !== '';

    if (f.required && !hasValue) { errors[f.key] = 'Required'; continue; }
    if (!hasValue) continue;

    let value = raw;
    switch (f.type) {
      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(raw))) { errors[f.key] = 'Invalid email'; continue; }
        break;
      case 'phone':
        value = String(raw).replace(/\D/g, '');
        if (value.length !== 10) { errors[f.key] = 'Must be 10 digits'; continue; }
        break;
      case 'number':
        value = Number(raw);
        if (isNaN(value)) { errors[f.key] = 'Must be a number'; continue; }
        break;
      case 'date':
        value = excelDateToISO(raw);
        if (!value) { errors[f.key] = 'Invalid date'; continue; }
        break;
      case 'enum':
        value = String(raw).trim().toUpperCase().replace(/\s+/g, '_');
        if (!f.options.includes(value)) { errors[f.key] = `Must be one of: ${f.options.join(', ')}`; continue; }
        break;
      case 'boolean': {
        const norm = String(raw).trim().toLowerCase();
        if (!['yes','no','true','false','y','n','1','0'].includes(norm)) { errors[f.key] = 'Use Yes/No'; continue; }
        value = ['yes','true','y','1'].includes(norm);
        break;
      }
      case 'select_department': {
        const match = departments.find(d => d.name.toLowerCase() === String(raw).trim().toLowerCase());
        if (!match) { errors[f.key] = 'Department not found'; continue; }
        value = match.id;
        break;
      }
      default:
        value = String(raw).trim();
    }

    if (f.target === 'column') payload[f.key] = value;
    else payload.extended_details[f.key] = value;
  }

  return { valid: Object.keys(errors).length === 0, errors, payload };
}

export function BulkUploadModal({ onClose, enabledList, departments }) {
  const [step, setStep] = useState('instructions'); // instructions | preview | result
  const [parsedRows, setParsedRows] = useState([]);
  const [validations, setValidations] = useState([]);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const bulkCreate = useBulkCreateEmployees();

  const fields = getBulkFields(enabledList);
  const requiredFields = fields.filter(f => f.required);
  const optionalFields = fields.filter(f => !f.required);

  const downloadTemplate = () => {
    const headers = fields.map(f => f.label);
    const sample = {};
    fields.forEach(f => { sample[f.label] = f.type === 'enum' ? f.options[0] : f.type === 'date' ? '2024-01-15' : f.type === 'boolean' ? 'Yes' : ''; });
    const ws = XLSX.utils.json_to_sheet([sample], { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, 'attendx-employee-bulk-upload-template.xlsx');
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!rows.length) { toast.error('The file has no data rows'); return; }
        const checked = rows.map(r => validateRow(r, fields, departments));
        setParsedRows(rows);
        setValidations(checked);
        setStep('preview');
      } catch {
        toast.error('Could not read this file — make sure it is a valid .xlsx file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validCount = validations.filter(v => v.valid).length;
  const invalidCount = validations.length - validCount;

  const handleSubmit = async () => {
    const payloads = validations.filter(v => v.valid).map(v => ({
      employment_type: 'FULL_TIME', ...v.payload,
    }));
    if (!payloads.length) { toast.error('No valid rows to upload'); return; }
    try {
      const res = await bulkCreate.mutateAsync(payloads);
      setResult(res.data);
      setStep('result');
    } catch {
      toast.error('Bulk upload failed — is employee-service running?');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-[16px] max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-card)]">
          <h2 className="text-[16px] font-bold text-[var(--text-primary)]">Bulk Upload Employees</h2>
          <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          {step === 'instructions' && (
            <>
              <p className="text-[13px] text-[var(--text-secondary)]">
                Download the template below — its columns match exactly what your company currently has enabled in
                <strong> Settings → Customize Employee Fields</strong>. Fill it in and upload it back here.
              </p>
              <div className="rounded-[10px] border border-[var(--border)] p-4 space-y-2">
                <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Mandatory Columns</p>
                <div className="flex flex-wrap gap-1.5">
                  {requiredFields.map(f => <span key={f.key} className="px-2 py-1 rounded-[6px] bg-[var(--danger)]/10 text-[var(--danger)] text-[11px] font-medium">{f.label}</span>)}
                </div>
                {optionalFields.length > 0 && (
                  <>
                    <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide pt-2">Optional Columns (enabled for your company)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {optionalFields.map(f => <span key={f.key} className="px-2 py-1 rounded-[6px] bg-[var(--bg-muted)] text-[var(--text-secondary)] text-[11px] font-medium">{f.label}</span>)}
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" icon={Download} onClick={downloadTemplate}>Download Template</Button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile}/>
                <Button icon={Upload} onClick={() => fileRef.current?.click()}>Upload Filled Sheet</Button>
              </div>
            </>
          )}

          {step === 'preview' && (
            <>
              <div className="flex items-center gap-4 text-[13px]">
                <span className="flex items-center gap-1.5 text-[var(--success)]"><CheckCircle2 size={14}/> {validCount} valid</span>
                {invalidCount > 0 && <span className="flex items-center gap-1.5 text-[var(--danger)]"><XCircle size={14}/> {invalidCount} will be skipped</span>}
                <span className="text-[var(--text-tertiary)]">{parsedRows.length} total rows</span>
              </div>
              <div className="border border-[var(--border)] rounded-[10px] overflow-auto max-h-[320px]">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-[var(--bg-muted)]">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      {fields.slice(0, 5).map(f => <th key={f.key} className="px-3 py-2 text-left whitespace-nowrap">{f.label}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {validations.map((v, i) => (
                      <tr key={i} className={v.valid ? '' : 'bg-[var(--danger)]/5'}>
                        <td className="px-3 py-2 text-[var(--text-tertiary)]">{i + 1}</td>
                        <td className="px-3 py-2">
                          {v.valid ? <CheckCircle2 size={14} className="text-[var(--success)]"/> : (
                            <span className="text-[var(--danger)]" title={Object.values(v.errors).join(', ')}>
                              {Object.entries(v.errors).map(([k, m]) => `${k}: ${m}`).join('; ')}
                            </span>
                          )}
                        </td>
                        {fields.slice(0, 5).map(f => <td key={f.key} className="px-3 py-2 whitespace-nowrap text-[var(--text-secondary)]">{String(parsedRows[i]?.[f.label] ?? '')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('instructions')}>Back</Button>
                <Button onClick={handleSubmit} loading={bulkCreate.isPending} disabled={!validCount}>
                  Upload {validCount} Employee{validCount !== 1 ? 's' : ''}
                </Button>
              </div>
            </>
          )}

          {step === 'result' && result && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="card text-center p-4"><p className="text-[22px] font-black text-[var(--text-primary)]">{result.total}</p><p className="text-[11px] text-[var(--text-tertiary)]">Total Rows</p></div>
                <div className="card text-center p-4"><p className="text-[22px] font-black text-[var(--success)]">{result.created}</p><p className="text-[11px] text-[var(--text-tertiary)]">Created</p></div>
                <div className="card text-center p-4"><p className="text-[22px] font-black text-[var(--danger)]">{result.failed}</p><p className="text-[11px] text-[var(--text-tertiary)]">Failed</p></div>
              </div>
              {result.failed > 0 && (
                <div className="border border-[var(--border)] rounded-[10px] divide-y divide-[var(--border)] max-h-[200px] overflow-auto">
                  {result.results.filter(r => r.status === 'failed').map(r => (
                    <div key={r.row} className="px-3 py-2 text-[12px] flex justify-between">
                      <span className="text-[var(--text-secondary)]">Row {r.row + 1}</span>
                      <span className="text-[var(--danger)]">{r.error}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={onClose}>Done</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
